import { cx } from '@linaria/core';
import { useRef } from 'react';
import { useCourseBuilder } from '@pm2/course-builder';
import { usePM2DisplayContext, pm2ConfigStore } from '@pm2/store';
import { useLocation } from '@provider/navigation';
import { isPlatformNodeJS } from '@utils/common';
import { UniversalContextMenu } from '@utils/component-framework';
import { useToggleState } from '@utils/react';
import { placeholderCls, togglerCls } from './_dropdown.styles.js';
import { CopyLink } from './copy-link.js';
import { LightboxButton } from './lightbox-button.js';
import { NewWindowButton } from './new-window-button.js';
import { NotebookButton } from './notebook-button.js';
import { PdfDownloadButton } from './pdf-download-button.js';
import { PdfExportButton } from './pdf-export-button.js';
import { TranscriptButton } from './transcript-button.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

function ToolbarDropdown(props) {
  const {
    inLibrary,
    inLightbox,
    isEmbedded
  } = usePM2DisplayContext();
  const previewMode = pm2ConfigStore.useSelector(state => state.previewMode);
  const portalId = pm2ConfigStore.useSelector(state => state.config.id);
  const location = useLocation();
  const showCourseBuilderPanel = useCourseBuilder(state => state.showPanel);
  const isComponentFrameworkNode = props.node.nodeType.startsWith("component-framework.");
  const togglerRef = useRef(null);
  const [active, actions] = useToggleState();
  const buttons = [(() => {
    if (isComponentFrameworkNode) {
      return null;
    }
    if (props.node.pm2.pdfFileUrl) {
      return /*#__PURE__*/jsx(PdfDownloadButton, {
        downloadUrl: props.node.pm2.pdfFileUrl
      }, "pdf");
    }
    if (
    // only text components can actually be exported
    props.node.nodeType === "text-component") {
      return /*#__PURE__*/jsx(PdfExportButton, {
        togglerRef: togglerRef,
        node: props.node
      }, "pdf");
    }
  })(), (() => {
    if (inLightbox) {
      // cannot open an element in lightbox if we're already in lightbox view
      return null;
    }
    if (inLightbox || !props.node.pm2.enableLightboxButton || isComponentFrameworkNode) {
      return null;
    }
    return /*#__PURE__*/jsx(LightboxButton, {
      node: props.node,
      speechFile: props.node.pm2.speechFileUrl,
      onClick: actions.disable
    }, "lightbox");
  })(), (() => {
    if (inLightbox) {
      // if we're in lightbox mode, then the copy URL feature becomes
      // equivalent to the new window button, and we therefore disable it
      //
      // this was discussed in ADO #108916
      return null;
    }
    if (!props.node.pm2.enableNewWindowButton || !props.node.pm2.learningObjectId) {
      return null;
    }
    if (location.pathname === `/learningObject/${props.node.pm2.learningObjectId}`) {
      // if the element has already been opened in a new window, then bail out
      // to avoid re-rendering the same element again
      return null;
    }
    return /*#__PURE__*/jsx(NewWindowButton, {
      node: props.node,
      learningObjectId: props.node.pm2.learningObjectId,
      onClick: actions.disable
    }, "new-window");
  })(), (() => {
    if (inLightbox) {
      // notebook button isn't available when content is opened in a lightbox
      // (this was discussed in ADO #108916)
      return null;
    }
    if (!props.node.pm2.enableNotebookButton || !props.node.pm2.learningObjectId || !props.node.pm2.learningObjectTitle) {
      return null;
    }
    return /*#__PURE__*/jsx(NotebookButton, {
      nodeType: props.node.nodeType,
      learningObjectId: props.node.pm2.learningObjectId,
      learningObjectTitle: props.node.pm2.learningObjectTitle,
      onClick: actions.disable
    }, "notebook");
  })(), (() => {
    if (!props.node.pm2.transcript?.length) {
      return null;
    }
    return /*#__PURE__*/jsx(TranscriptButton, {
      nodeType: props.node.nodeType,
      transcript: props.node.pm2.transcript,
      onClick: actions.disable
    }, "transcript");
  })()].filter(it => !!it);
  const copyLink = (() => {
    const id = props.node.pm2.learningObjectId || props.node.id;
    if (!id) {
      return;
    }
    if (inLibrary || inLightbox && !props.node.pm2.learningObjectId) {
      // copying direct links from library is not supported; here link copying
      // is handled by the built-in functionality for that purpose
      // same goes for lightbox-elements that do not have a standalone page.
      return null;
    }
    const url = (() => {
      if (inLightbox) {
        // if the content element was opened in a lightbox, we cannot generate
        // a hash-link that scrolls the user to it, therefore we instead create
        // a link to the element opened on a standalone page (equivalent to
        // the open in new window feature)
        //
        // this was discussed in ADO #108916
        const previewPrefix = previewMode ? `/${portalId}` : "";
        const url = new URL(isPlatformNodeJS() ? "http://localhost" : window.location.href);
        url.pathname = `${previewPrefix}/learningObject/${props.node.pm2.learningObjectId}`;
        return url;
      }
      const url = new URL(isPlatformNodeJS() ? "http://localhost" : window.location.href);
      url.hash = id;
      return url;
    })();
    return /*#__PURE__*/jsx(CopyLink, {
      href: url.toString()
    });
  })();
  if (buttons.length === 0 && !copyLink) {
    return null;
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [!isComponentFrameworkNode && !props.customPosition && inLibrary && /*#__PURE__*/jsx("span", {
      "data-toolbar-placeholder": true,
      className: placeholderCls
    }), /*#__PURE__*/jsx(UniversalContextMenu, {
      togglerRef: togglerRef,
      layout: "solid",
      label: "\xC5bn kontekstmenuen",
      enabled: !showCourseBuilderPanel,
      active: active && !showCourseBuilderPanel,
      togglerClassName: cx(togglerCls, "element-toolbar", inLibrary && "inLibrary", inLightbox && "inLightbox", isEmbedded && "isEmbedded", (isComponentFrameworkNode || props.customPosition) && "customPosition", showCourseBuilderPanel && "hidden"),
      footer: copyLink,
      onToggle: actions.toggle,
      onClose: actions.disable,
      children: buttons
    })]
  });
}

export { ToolbarDropdown };
//# sourceMappingURL=_dropdown.js.map
